import { JsonFormsCore } from '@jsonforms/core';
import { JsonForms } from '@jsonforms/react';
import { vanillaCells, vanillaRenderers } from '@jsonforms/vanilla-renderers';
import { ErrorObject } from 'ajv';
import { isEqual } from 'lodash';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmbeddedForm } from '../../api/graphql/generated';
import { RightAlignedButtons } from '../button/aligned-buttons';
import { LoadingButton } from '../button/loading-button';
import { ionicRenderers } from '../forms/ionic-renders';

interface ApplicationLicenseFormProps {
  initialFormData: FormData;
  form: EmbeddedForm;
  onSubmit: (formData: FormData) => Promise<void>;
  disableSubmit?: boolean;
  isLoading?: boolean;
}

export const ApplicationLicenseForm = (
  props: ApplicationLicenseFormProps
): ReactElement => {
  const { i18n } = useTranslation();
  const { initialFormData, form, disableSubmit, isLoading, onSubmit } = props;

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<ErrorObject[] | undefined>();

  const onChange = (formState: Pick<JsonFormsCore, 'data' | 'errors'>) => {
    const { data, errors } = formState;
    if (!isEqual(data, formData)) {
      setFormData(data);
    }
    if (!isEqual(errors, formErrors)) {
      setFormErrors(errors as unknown as ErrorObject[]);
    }
  };

  const submitForm = useCallback(() => {
    onSubmit(formData);
  }, [formData, onSubmit]);

  useEffect(() => {
    if (initialFormData) {
      setFormData(initialFormData);
    }
  }, [initialFormData]);

  const hasErrors = formErrors ? formErrors.length > 0 : false;

  return (
    <>
      <JsonForms
        data={formData}
        schema={form.schema}
        uischema={form.uischema}
        renderers={[...ionicRenderers, ...vanillaRenderers]}
        cells={vanillaCells}
        onChange={(state) => onChange(state)}
      />
      <RightAlignedButtons>
        <LoadingButton
          loading={isLoading ?? false}
          buttonProps={{
            type: 'submit',
            disabled: disableSubmit || hasErrors,
            onClick: (e) => {
              e.preventDefault();
              e.stopPropagation();
              submitForm();
            },
          }}
        >
          {i18n.t('common:assign')}
        </LoadingButton>
      </RightAlignedButtons>
    </>
  );
};
